import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import grassTileImg from '@/res/tiles/isometric_0087.png';
import plantsImgUrl from '@/res/tiles/craftpix-net-200380-free-pixel-art-plants-for-farm/PNG/Plants.png';
import {
  COLS,
  GROWTH_CHAIN,
  ROWS,
  TILE_DEPTH,
  TILE_H,
  TILE_W,
  clearGarden,
  countTiles,
  createInitialGarden,
  getGridCell,
  getGrowthSummary,
  groupTiles,
  isoPoint,
  randomizeGarden,
  stepGrowth,
} from './isometricGardenModel';

const OFF_X = (COLS * TILE_W) / 2 + 20;
const OFF_Y = 70;
const CANVAS_W = COLS * TILE_W + 80;
const CANVAS_H = ((COLS + ROWS) * TILE_H) / 2 + TILE_DEPTH + 110;
const ANIMATION_DURATION = 520;
const STAGGER_MS = 28;

function drawIsoTop(ctx, x, y, fill, stroke) {
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + TILE_W / 2, y + TILE_H / 2);
  ctx.lineTo(x + TILE_W, y);
  ctx.lineTo(x + TILE_W / 2, y - TILE_H / 2);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 0.8;
    ctx.stroke();
  }
}

function drawIsoLeft(ctx, x, y, fill, stroke) {
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + TILE_W / 2, y + TILE_H / 2);
  ctx.lineTo(x + TILE_W / 2, y + TILE_H / 2 + TILE_DEPTH);
  ctx.lineTo(x, y + TILE_DEPTH);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 0.8;
    ctx.stroke();
  }
}

function drawIsoRight(ctx, x, y, fill, stroke) {
  ctx.beginPath();
  ctx.moveTo(x + TILE_W, y);
  ctx.lineTo(x + TILE_W / 2, y + TILE_H / 2);
  ctx.lineTo(x + TILE_W / 2, y + TILE_H / 2 + TILE_DEPTH);
  ctx.lineTo(x + TILE_W, y + TILE_DEPTH);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 0.8;
    ctx.stroke();
  }
}

function drawBaseTile(ctx, x, y, topColor, leftColor, rightColor, strokeColor = 'rgba(60,50,30,0.25)') {
  drawIsoLeft(ctx, x, y, leftColor, strokeColor);
  drawIsoRight(ctx, x, y, rightColor, strokeColor);
  drawIsoTop(ctx, x, y, topColor, strokeColor);
}

function drawTreeCanopy(ctx, cx, cy, colors) {
  const blobs = [
    { x: cx, y: cy - 50, radius: 18 },
    { x: cx - 14, y: cy - 40, radius: 12 },
    { x: cx + 14, y: cy - 34, radius: 11 },
  ];

  blobs.forEach((blob, index) => {
    const gradient = ctx.createRadialGradient(blob.x - 4, blob.y - 6, 2, blob.x, blob.y, blob.radius);
    gradient.addColorStop(0, colors.light);
    gradient.addColorStop(0.65, colors.mid);
    gradient.addColorStop(1, colors.shadow);
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(blob.x, blob.y, blob.radius - index, 0, Math.PI * 2);
    ctx.fill();
  });
}

function drawDetailedTrunk(ctx, cx, cy, width, color, shadowColor, path) {
  ctx.save();
  ctx.strokeStyle = shadowColor;
  ctx.lineWidth = width + 4;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  path(ctx);
  ctx.stroke();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  path(ctx);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(210,160,80,0.22)';
  ctx.lineWidth = Math.max(1.5, width * 0.3);
  path(ctx);
  ctx.stroke();
  ctx.restore();
}

function drawBaseRocks(ctx, cx, cy) {
  const rocks = [
    { x: cx - 16, y: cy + 4, rx: 9, ry: 6, rot: -0.2 },
    { x: cx + 14, y: cy + 5, rx: 8, ry: 5, rot: 0.3 },
    { x: cx - 4, y: cy + 8, rx: 11, ry: 6, rot: 0.1 },
    { x: cx - 22, y: cy + 8, rx: 6, ry: 4, rot: -0.4 },
    { x: cx + 20, y: cy + 8, rx: 6, ry: 4, rot: 0.4 },
  ];

  rocks.forEach(({ x, y, rx, ry, rot }) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rot);
    ctx.fillStyle = 'rgba(40,30,20,0.25)';
    ctx.beginPath();
    ctx.ellipse(2, 3, rx, ry * 0.7, 0, 0, Math.PI * 2);
    ctx.fill();
    const gradient = ctx.createRadialGradient(-rx * 0.3, -ry * 0.4, 0, 0, 0, rx);
    gradient.addColorStop(0, '#d0cabb');
    gradient.addColorStop(0.6, '#a8a090');
    gradient.addColorStop(1, '#706860');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(50,40,30,0.3)';
    ctx.lineWidth = 0.8;
    ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.18)';
    ctx.beginPath();
    ctx.ellipse(-rx * 0.25, -ry * 0.3, rx * 0.4, ry * 0.3, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  });
}

function drawPolyFoliage(ctx, points, fillColor, lightColor, shadowColor) {
  const total = points.length;
  let centerX = 0;
  let centerY = 0;

  points.forEach(([pointX, pointY]) => {
    centerX += pointX;
    centerY += pointY;
  });

  centerX /= total;
  centerY /= total;

  for (let index = 0; index < total; index += 1) {
    const [ax, ay] = points[index];
    const [bx, by] = points[(index + 1) % total];
    const midpointY = (ay + by) / 2;
    const facing = (centerY - midpointY) / 30;
    const cross = (bx - ax) * (centerY - ay) - (by - ay) * (centerX - ax);
    const faceColor = facing > 0.3 ? lightColor : (cross < 0 ? shadowColor : fillColor);

    ctx.save();
    ctx.fillStyle = faceColor;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(ax, ay);
    ctx.lineTo(bx, by);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.12)';
    ctx.lineWidth = 0.7;
    ctx.stroke();
    ctx.restore();
  }
}

function polyRing(cx, cy, radius, sides, jitter, angleOffset) {
  const points = [];
  for (let index = 0; index < sides; index += 1) {
    const angle = angleOffset + (index / sides) * Math.PI * 2;
    const radiusWithJitter = radius + Math.sin(index * 7.3 + angleOffset * 3) * jitter;
    points.push([
      cx + Math.cos(angle) * radiusWithJitter,
      cy + Math.sin(angle) * radiusWithJitter,
    ]);
  }
  return points;
}

// ── Plant sprite sheet ──────────────────────────────────────────────────────
let _plantsImg = null;
export function setPlantsImage(img) { _plantsImg = img; }

// Source rects from Plants.png (768×432) — extracted via bounding-box scan.
// Row 0, sprites [4]→[8]: tree growth (seed→full tree).
// Source heights cropped to tree+trunk only, excluding ground decoration ring.
// Display sizes scale up progressively to show growth.
// Source rects from grid-cell bounding-box scan of Plants.png (768×432).
const PLANT_SPRITE_MAP = {
  // Legacy tree growth chain — kept for backward compat (= treeA_1..5)
  seed:        { sx: 135, sy:  54, sw:  20, sh:  20, dw: 22, dh: 26 },
  sprout:      { sx: 161, sy:  42, sw:  29, sh:  36, dw: 32, dh: 42 },
  sapling:     { sx: 194, sy:  29, sw:  60, sh:  51, dw: 42, dh: 52 },
  young_tree:  { sx: 256, sy:  14, sw:  57, sh:  66, dw: 52, dh: 66 },
  oak:         { sx: 324, sy:  14, sw:  53, sh:  66, dw: 60, dh: 76 },
  fruit_tree:  { sx: 100, sy:   9, sw:  24, sh:  37, dw: 50, dh: 70 },
  palm:        { sx: 256, sy: 219, sw:  57, sh:  69, dw: 54, dh: 74 },

  // ── Species A (row 0, cols 3-7): deciduous tree ──
  treeA_1:     { sx: 135, sy:  54, sw:  20, sh:  20, dw: 22, dh: 26 },
  treeA_2:     { sx: 161, sy:  42, sw:  29, sh:  36, dw: 32, dh: 42 },
  treeA_3:     { sx: 194, sy:  29, sw:  60, sh:  51, dw: 42, dh: 56 },
  treeA_4:     { sx: 256, sy:  14, sw:  57, sh:  66, dw: 50, dh: 68 },
  treeA_5:     { sx: 324, sy:  14, sw:  53, sh:  66, dw: 58, dh: 76 },

  // ── Species B (row 1, cols 3-7): deciduous tree B ──
  treeB_1:     { sx: 135, sy: 119, sw:  20, sh:  20, dw: 22, dh: 26 },
  treeB_2:     { sx: 161, sy: 108, sw:  29, sh:  33, dw: 30, dh: 40 },
  treeB_3:     { sx: 194, sy:  96, sw:  60, sh:  47, dw: 44, dh: 58 },
  treeB_4:     { sx: 256, sy:  82, sw:  57, sh:  62, dw: 52, dh: 70 },
  treeB_5:     { sx: 324, sy:  82, sw:  53, sh:  62, dw: 58, dh: 76 },

  // ── Species C (row 2, cols 3-7): bush / shrub ──
  treeC_1:     { sx: 135, sy: 180, sw:  20, sh:  22, dw: 22, dh: 28 },
  treeC_2:     { sx: 161, sy: 172, sw:  29, sh:  32, dw: 32, dh: 42 },
  treeC_3:     { sx: 194, sy: 162, sw:  60, sh:  44, dw: 44, dh: 56 },
  treeC_4:     { sx: 256, sy: 149, sw:  57, sh:  59, dw: 50, dh: 68 },
  treeC_5:     { sx: 324, sy: 149, sw:  53, sh:  59, dw: 58, dh: 76 },

  // ── Species D (row 3, cols 3-7): palm tree ──
  treeD_1:     { sx: 135, sy: 263, sw:  20, sh:  20, dw: 22, dh: 28 },
  treeD_2:     { sx: 161, sy: 254, sw:  29, sh:  30, dw: 32, dh: 42 },
  treeD_3:     { sx: 194, sy: 236, sw:  60, sh:  49, dw: 44, dh: 58 },
  treeD_4:     { sx: 256, sy: 219, sw:  57, sh:  69, dw: 52, dh: 70 },
  treeD_5:     { sx: 324, sy: 219, sw:  53, sh:  69, dw: 58, dh: 76 },

  // Legacy family sprites (backward compat for old saved grids)
  vine_1:      { sx:   8, sy:  79, sw:  15, sh:  15, dw: 26, dh: 28 },
  vine_2:      { sx:  38, sy:  75, sw:  20, sh:  19, dw: 34, dh: 34 },
  vine_3:      { sx:  70, sy:  57, sw:  20, sh:  37, dw: 36, dh: 52 },
  pepper_1:    { sx:   8, sy: 190, sw:  13, sh:  16, dw: 26, dh: 30 },
  pepper_2:    { sx:  38, sy: 180, sw:  20, sh:  26, dw: 34, dh: 40 },
  pepper_3:    { sx:  65, sy: 172, sw:  27, sh:  34, dw: 44, dh: 50 },
  herb_1:      { sx:  11, sy: 112, sw:  12, sh:  14, dw: 24, dh: 28 },
  herb_2:      { sx:  40, sy: 105, sw:  19, sh:  21, dw: 32, dh: 36 },
  herb_3:      { sx:  68, sy:  96, sw:  23, sh:  30, dw: 40, dh: 46 },
  flower_1:    { sx:   9, sy: 416, sw:  14, sh:  14, dw: 20, dh: 20 },
  flower_2:    { sx:  37, sy: 410, sw:  22, sh:  20, dw: 28, dh: 28 },
  flower_3:    { sx:  64, sy: 396, sw:  64, sh:  34, dw: 40, dh: 38 },
  flower_4:    { sx: 135, sy: 341, sw:  20, sh:  87, dw: 36, dh: 48 },

  // Decorative tiles
  bush:        { sx: 161, sy: 254, sw:  29, sh:  30, dw: 34, dh: 36 },
  cherry:      { sx:  64, sy: 327, sw:  64, sh: 103, dw: 48, dh: 68 },
  autumn_tree: { sx: 260, sy: 219, sw:  53, sh: 105, dw: 46, dh: 64 },
};

function drawPlantSprite(ctx, tileType, x, y) {
  const spec = PLANT_SPRITE_MAP[tileType];
  if (!_plantsImg || !spec) return;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(
    _plantsImg,
    spec.sx, spec.sy, spec.sw, spec.sh,
    x + TILE_W / 2 - spec.dw / 2,
    y - spec.dh + 6,
    spec.dw, spec.dh,
  );
}

const tileRenderers = {
  empty() {},
  grass(ctx, x, y) {
    const hw = TILE_W / 2;
    const hh = TILE_H / 2;
    const dep = TILE_DEPTH;

    // ── Helper: clip to a face polygon ──────────────────────────────────
    function clipLeft() {
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + hw, y + hh);
      ctx.lineTo(x + hw, y + hh + dep);
      ctx.lineTo(x, y + dep);
      ctx.closePath();
      ctx.clip();
    }
    function clipRight() {
      ctx.beginPath();
      ctx.moveTo(x + TILE_W, y);
      ctx.lineTo(x + hw, y + hh);
      ctx.lineTo(x + hw, y + hh + dep);
      ctx.lineTo(x + TILE_W, y + dep);
      ctx.closePath();
      ctx.clip();
    }
    function clipTop() {
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + hw, y + hh);
      ctx.lineTo(x + TILE_W, y);
      ctx.lineTo(x + hw, y - hh);
      ctx.closePath();
      ctx.clip();
    }

    // ── Left face: warm orange-brown dirt (lit side) ─────────────────────
    const leftGrad = ctx.createLinearGradient(x, y, x, y + dep);
    leftGrad.addColorStop(0,   '#c87832');
    leftGrad.addColorStop(0.25,'#b86828');
    leftGrad.addColorStop(0.55,'#9e5420');
    leftGrad.addColorStop(0.8, '#844018');
    leftGrad.addColorStop(1,   '#5e2c0e');
    drawIsoLeft(ctx, x, y, leftGrad, 'rgba(10,4,0,0.5)');

    // Left face texture: horizontal strata bands
    ctx.save();
    clipLeft();
    const strataCount = 7;
    for (let i = 0; i <= strataCount; i++) {
      const t = i / strataCount;
      const sy = y + t * dep;
      // alternating light/dark bands
      ctx.globalAlpha = i % 2 === 0 ? 0.12 : 0.22;
      ctx.strokeStyle = i % 2 === 0 ? '#f0b060' : '#4a1e06';
      ctx.lineWidth = i % 2 === 0 ? 1.8 : 1.2;
      ctx.beginPath();
      // strata follow iso-left slope
      ctx.moveTo(x,      sy);
      ctx.lineTo(x + hw, sy + hh * 0.5);
      ctx.stroke();
    }
    // dark specks
    ctx.globalAlpha = 0.2;
    [[x+10,y+14],[x+24,y+22],[x+8,y+34],[x+28,y+42],[x+14,y+50]].forEach(([px,py]) => {
      ctx.fillStyle = '#2e1006';
      ctx.beginPath();
      ctx.ellipse(px, py, 5, 3, 0.3, 0, Math.PI * 2);
      ctx.fill();
    });
    // bright orange highlight near top of left face
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = '#f0a050';
    ctx.beginPath();
    ctx.moveTo(x, y + 4);
    ctx.lineTo(x + hw, y + hh + 4);
    ctx.lineTo(x + hw, y + hh + 12);
    ctx.lineTo(x, y + 12);
    ctx.closePath();
    ctx.fill();
    // green grass lip at very top of left face
    ctx.globalAlpha = 1;
    const lgStrip = ctx.createLinearGradient(x, y, x + hw, y + hh);
    lgStrip.addColorStop(0, '#78c83c');
    lgStrip.addColorStop(1, '#5cac28');
    ctx.fillStyle = lgStrip;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + hw, y + hh);
    ctx.lineTo(x + hw, y + hh + 6);
    ctx.lineTo(x, y + 6);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // ── Right face: darker orange-brown (shadow side) ───────────────────
    const rightGrad = ctx.createLinearGradient(x + TILE_W, y, x + TILE_W, y + dep);
    rightGrad.addColorStop(0,   '#a85e24');
    rightGrad.addColorStop(0.25,'#944e1c');
    rightGrad.addColorStop(0.55,'#7a3e14');
    rightGrad.addColorStop(0.8, '#602e0c');
    rightGrad.addColorStop(1,   '#3e1c06');
    drawIsoRight(ctx, x, y, rightGrad, 'rgba(10,4,0,0.5)');

    // Right face texture: horizontal strata bands
    ctx.save();
    clipRight();
    for (let i = 0; i <= strataCount; i++) {
      const t = i / strataCount;
      const sy = y + t * dep;
      ctx.globalAlpha = i % 2 === 0 ? 0.1 : 0.2;
      ctx.strokeStyle = i % 2 === 0 ? '#d08040' : '#2e0e02';
      ctx.lineWidth = i % 2 === 0 ? 1.8 : 1.2;
      ctx.beginPath();
      ctx.moveTo(x + TILE_W, sy);
      ctx.lineTo(x + hw,     sy + hh * 0.5);
      ctx.stroke();
    }
    ctx.globalAlpha = 0.18;
    [[x+TILE_W-10,y+14],[x+TILE_W-24,y+24],[x+TILE_W-8,y+36],[x+TILE_W-22,y+44]].forEach(([px,py]) => {
      ctx.fillStyle = '#1e0802';
      ctx.beginPath();
      ctx.ellipse(px, py, 5, 3, -0.3, 0, Math.PI * 2);
      ctx.fill();
    });
    // green grass lip at very top of right face
    ctx.globalAlpha = 1;
    const rgStrip = ctx.createLinearGradient(x + TILE_W, y, x + hw, y + hh);
    rgStrip.addColorStop(0, '#5cac28');
    rgStrip.addColorStop(1, '#4a9020');
    ctx.fillStyle = rgStrip;
    ctx.beginPath();
    ctx.moveTo(x + TILE_W, y);
    ctx.lineTo(x + hw, y + hh);
    ctx.lineTo(x + hw, y + hh + 6);
    ctx.lineTo(x + TILE_W, y + 6);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // ── Top face: bright lime-green base ─────────────────────────────────
    const topGrad = ctx.createLinearGradient(x + hw, y - hh, x + hw, y + hh);
    topGrad.addColorStop(0,   '#a0d840');
    topGrad.addColorStop(0.4, '#8cc438');
    topGrad.addColorStop(1,   '#72aa28');
    drawIsoTop(ctx, x, y, topGrad, 'rgba(8,28,0,0.55)');

    // Top face: rounded grass-blob patches in a 3×3 soft grid
    ctx.save();
    clipTop();
    // The iso-diamond centre is at (x+hw, y), spanning ±hw horizontally, ±hh vertically.
    // Place 9 blobs in a 3×3 arrangement mapped to iso coords.
    const blobData = [
      // [isoX offset, isoY offset, rx, ry]  — relative to tile centre
      [-20, -8,  9, 4.5],
      [  0, -10, 9, 4.5],
      [ 20, -8,  9, 4.5],
      [-20,  0,  9, 4.5],
      [  0,  0,  10, 5],
      [ 20,  0,  9, 4.5],
      [-16,  8,  8, 4],
      [  0,  8,  9, 4.5],
      [ 16,  8,  8, 4],
    ];
    const cx0 = x + hw;
    const cy0 = y;
    blobData.forEach(([dx, dy, rw, rh]) => {
      // darker mottling blob
      ctx.globalAlpha = 0.38;
      ctx.fillStyle = '#4a7e18';
      ctx.beginPath();
      ctx.ellipse(cx0 + dx, cy0 + dy, rw, rh, 0, 0, Math.PI * 2);
      ctx.fill();
      // inner bright highlight
      ctx.globalAlpha = 0.18;
      ctx.fillStyle = '#c0e858';
      ctx.beginPath();
      ctx.ellipse(cx0 + dx - rw * 0.25, cy0 + dy - rh * 0.3, rw * 0.45, rh * 0.45, 0, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  },
  dirt(ctx, x, y) {
    const hw = TILE_W / 2;
    const hh = TILE_H / 2;
    const dep = TILE_DEPTH;

    const dLeftGrad = ctx.createLinearGradient(x, y, x, y + dep);
    dLeftGrad.addColorStop(0, '#b87040');
    dLeftGrad.addColorStop(1, '#5e2e10');
    const dRightGrad = ctx.createLinearGradient(x + TILE_W, y, x + TILE_W, y + dep);
    dRightGrad.addColorStop(0, '#8c5228');
    dRightGrad.addColorStop(1, '#3a1c08');
    const dTopGrad = ctx.createRadialGradient(x + hw - 6, y - hh * 0.2, 0, x + hw, y, hw);
    dTopGrad.addColorStop(0, '#b08850');
    dTopGrad.addColorStop(0.6, '#9a7240');
    dTopGrad.addColorStop(1, '#765230');

    drawIsoLeft(ctx, x, y, dLeftGrad, 'rgba(30,14,4,0.4)');
    drawIsoRight(ctx, x, y, dRightGrad, 'rgba(30,14,4,0.4)');
    drawIsoTop(ctx, x, y, dTopGrad, 'rgba(30,14,4,0.4)');

    // dirt speckles on top
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + hw, y + hh);
    ctx.lineTo(x + TILE_W, y);
    ctx.lineTo(x + hw, y - hh);
    ctx.closePath();
    ctx.clip();
    ctx.globalAlpha = 0.28;
    [[x+hw,y-4],[x+hw-14,y+3],[x+hw+13,y+2],[x+hw-4,y+8],[x+hw+4,y-8]].forEach(([px,py]) => {
      ctx.fillStyle = '#3e2006';
      ctx.beginPath();
      ctx.ellipse(px, py, 6, 3, 0, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  },
  stone_path(ctx, x, y) {
    drawBaseTile(ctx, x, y, '#b0ab9e', '#7a7670', '#8a8680', 'rgba(40,40,40,0.25)');
    ctx.save();
    ctx.globalAlpha = 0.32;
    ctx.strokeStyle = '#5a5850';
    ctx.lineWidth = 0.8;
    const cx = x + TILE_W / 2;
    ctx.beginPath();
    ctx.moveTo(cx, y - TILE_H / 2 + 4);
    ctx.lineTo(cx - 10, y + 4);
    ctx.lineTo(cx + TILE_W / 2 - 2, y + TILE_H / 2 - 4);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx - 2, y - 2);
    ctx.lineTo(cx + 16, y + 10);
    ctx.stroke();
    ctx.restore();
  },
  sand_raked(ctx, x, y) {
    drawBaseTile(ctx, x, y, '#e8dfc8', '#c8b898', '#d4c4a8', 'rgba(100,80,40,0.2)');
    ctx.save();
    ctx.globalAlpha = 0.3;
    ctx.strokeStyle = '#a89060';
    ctx.lineWidth = 0.7;
    for (let offset = -2; offset <= 2; offset += 1) {
      const rowY = y + offset * 6;
      ctx.beginPath();
      ctx.moveTo(x + 6, rowY);
      ctx.lineTo(x + TILE_W - 6, rowY);
      ctx.stroke();
    }
    ctx.restore();
  },
  water(ctx, x, y) {
    drawIsoLeft(ctx, x, y, '#2a6080', 'rgba(20,50,80,0.3)');
    drawIsoRight(ctx, x, y, '#1e4f6a', 'rgba(20,50,80,0.3)');
    const gradient = ctx.createLinearGradient(x, y - TILE_H / 2, x + TILE_W, y + TILE_H / 2);
    gradient.addColorStop(0, '#4ca8d8');
    gradient.addColorStop(0.5, '#2e88bb');
    gradient.addColorStop(1, '#1e6898');
    drawIsoTop(ctx, x, y, gradient, 'rgba(10,40,80,0.3)');
    ctx.save();
    ctx.globalAlpha = 0.35;
    [[x + TILE_W / 2 - 10, y - 4], [x + TILE_W / 2 + 6, y - 8], [x + TILE_W / 2, y + 2]].forEach(([px, py]) => {
      ctx.fillStyle = '#a8e4f8';
      ctx.beginPath();
      ctx.ellipse(px, py, 5, 2, 0.3, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  },
  seed(ctx, x, y) {
    if (_plantsImg) { drawPlantSprite(ctx, 'seed', x, y); return; }
    tileRenderers.dirt(ctx, x, y);
    const cx = x + TILE_W / 2;
    const cy = y + 2;
    ctx.fillStyle = '#8a6020';
    ctx.beginPath();
    ctx.ellipse(cx, cy + 2, 6, 4, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#6ab840';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(cx + 1, cy - 4);
    ctx.quadraticCurveTo(cx - 1, cy - 8, cx, cy - 12);
    ctx.stroke();
  },
  sprout(ctx, x, y) {
    if (_plantsImg) { drawPlantSprite(ctx, 'sprout', x, y); return; }
    tileRenderers.dirt(ctx, x, y);
    const cx = x + TILE_W / 2;
    const cy = y + 2;
    ctx.strokeStyle = '#4a8d2d';
    ctx.lineWidth = 2.2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.bezierCurveTo(cx - 2, cy - 8, cx + 3, cy - 14, cx, cy - 22);
    ctx.stroke();
    ['#9ad85f', '#84c94d'].forEach((color, index) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      const direction = index === 0 ? -1 : 1;
      ctx.ellipse(cx + direction * 7, cy - 16, 8, 4, direction * 0.5, 0, Math.PI * 2);
      ctx.fill();
    });
  },
  sapling(ctx, x, y) {
    if (_plantsImg) { drawPlantSprite(ctx, 'sapling', x, y); return; }
    tileRenderers.grass(ctx, x, y);
    const cx = x + TILE_W / 2;
    const cy = y;
    ctx.strokeStyle = '#8a5a28';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(cx, cy + 6);
    ctx.bezierCurveTo(cx - 3, cy - 8, cx + 4, cy - 18, cx + 1, cy - 28);
    ctx.stroke();
    drawPolyFoliage(ctx, polyRing(cx, cy - 22, 10, 7, 3, 0.3), '#4ea034', '#88ca58', '#1a5416');
    drawPolyFoliage(ctx, polyRing(cx + 2, cy - 30, 8, 6, 2, 1.0), '#56ac3c', '#90d464', '#1e5c1a');
  },
  young_tree(ctx, x, y) {
    if (_plantsImg) { drawPlantSprite(ctx, 'young_tree', x, y); return; }
    tileRenderers.grass(ctx, x, y);
    const cx = x + TILE_W / 2;
    const cy = y - 2;
    ctx.strokeStyle = '#7a4d25';
    ctx.lineWidth = 7;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(cx, cy + 8);
    ctx.bezierCurveTo(cx - 4, cy - 10, cx + 5, cy - 30, cx + 1, cy - 52);
    ctx.stroke();
    [[cx - 10, cy + 10, 5, 3], [cx + 12, cy + 9, 4, 3]].forEach(([rx, ry, w, h]) => {
      ctx.fillStyle = '#a8a090';
      ctx.beginPath();
      ctx.ellipse(rx, ry, w, h, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(50,40,30,0.3)';
      ctx.lineWidth = 0.8;
      ctx.stroke();
    });
    drawPolyFoliage(ctx, polyRing(cx, cy - 38, 13, 9, 4, 0.2), '#4ea034', '#88ca58', '#1a5416');
    drawPolyFoliage(ctx, polyRing(cx - 8, cy - 30, 10, 8, 3, 1.0), '#52a436', '#8ccc5c', '#1c5616');
    drawPolyFoliage(ctx, polyRing(cx + 10, cy - 32, 10, 8, 3, 0.7), '#52a838', '#8ed060', '#1c5818');
    drawPolyFoliage(ctx, polyRing(cx + 1, cy - 48, 9, 7, 3, 1.5), '#64b844', '#a4dc6c', '#225e1c');
  },
  bonsai(ctx, x, y) {
    if (_plantsImg) { drawPlantSprite(ctx, 'bonsai', x, y); return; }
    tileRenderers.stone_path(ctx, x, y);
    const cx = x + TILE_W / 2;
    const cy = y + 6;
    drawDetailedTrunk(ctx, cx, cy, 7, '#8c5a24', '#1e0c04',
      (canvasContext) => {
        canvasContext.beginPath();
        canvasContext.moveTo(cx - 3, cy + 4);
        canvasContext.bezierCurveTo(cx - 9, cy - 12, cx + 12, cy - 24, cx + 5, cy - 40);
      });
    ctx.save();
    ctx.strokeStyle = 'rgba(15,5,2,0.35)';
    ctx.lineWidth = 1;
    ctx.lineCap = 'round';
    [[cx - 4, cy + 1, cx - 6, cy - 10], [cx + 2, cy - 5, cx + 7, cy - 18]].forEach(([x1, y1, x2, y2]) => {
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    });
    ctx.restore();
    [[cx - 12, cy + 8, 7, 4, -0.2], [cx + 10, cy + 9, 6, 4, 0.3], [cx - 2, cy + 10, 8, 5, 0.1]].forEach(([rx, ry, w, h, rot]) => {
      ctx.save();
      ctx.translate(rx, ry);
      ctx.rotate(rot);
      const gradient = ctx.createRadialGradient(-w * 0.3, -h * 0.4, 0, 0, 0, w);
      gradient.addColorStop(0, '#d0cabb');
      gradient.addColorStop(0.6, '#a8a090');
      gradient.addColorStop(1, '#706860');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.ellipse(0, 0, w, h, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(50,40,30,0.3)';
      ctx.lineWidth = 0.8;
      ctx.stroke();
      ctx.restore();
    });
    drawPolyFoliage(ctx, polyRing(cx + 6, cy - 32, 11, 8, 3, 0.4), '#4ea034', '#88ca58', '#1a5416');
    drawPolyFoliage(ctx, polyRing(cx - 4, cy - 24, 10, 7, 3, 1.2), '#348020', '#5a9e38', '#124010');
    drawPolyFoliage(ctx, polyRing(cx + 14, cy - 26, 9, 7, 3, 0.8), '#52a436', '#8ccc5c', '#1c5616');
    drawPolyFoliage(ctx, polyRing(cx + 4, cy - 42, 9, 7, 3, 1.6), '#56ac3c', '#90d464', '#1e5c1a');
  },
  ancient_bonsai(ctx, x, y) {
    tileRenderers.oak(ctx, x, y);
  },
  bush(ctx, x, y) {
    if (_plantsImg) { drawPlantSprite(ctx, 'bush', x, y); return; }
    tileRenderers.grass(ctx, x, y);
    drawTreeCanopy(ctx, x + TILE_W / 2, y + 18, { light: '#95d766', mid: '#4f9c40', shadow: '#24551b' });
  },
  rock(ctx, x, y) {
    tileRenderers.grass(ctx, x, y);
    const cx = x + TILE_W / 2;
    const cy = y + 10;
    const gradient = ctx.createRadialGradient(cx - 6, cy - 4, 2, cx, cy, 18);
    gradient.addColorStop(0, '#d1cbc0');
    gradient.addColorStop(0.65, '#9d978b');
    gradient.addColorStop(1, '#6e675e');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.ellipse(cx, cy, 18, 11, -0.15, 0, Math.PI * 2);
    ctx.fill();
  },
  lantern(ctx, x, y) {
    tileRenderers.stone_path(ctx, x, y);
    const cx = x + TILE_W / 2;
    const cy = y - 6;
    ctx.strokeStyle = '#78634a';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(cx, cy + 26);
    ctx.lineTo(cx, cy - 8);
    ctx.stroke();
    ctx.fillStyle = '#eadfbe';
    ctx.fillRect(cx - 8, cy - 8, 16, 16);
    ctx.fillStyle = '#e8b84b';
    ctx.beginPath();
    ctx.arc(cx, cy, 5, 0, Math.PI * 2);
    ctx.fill();
  },
  pagoda(ctx, x, y) {
    tileRenderers.stone_path(ctx, x, y);
    const cx = x + TILE_W / 2;
    const cy = y - 8;
    ctx.fillStyle = '#cfc7b2';
    ctx.fillRect(cx - 12, cy + 10, 24, 16);
    ctx.fillStyle = '#7a4f37';
    ctx.beginPath();
    ctx.moveTo(cx - 18, cy + 10);
    ctx.lineTo(cx, cy - 2);
    ctx.lineTo(cx + 18, cy + 10);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(cx - 14, cy - 2);
    ctx.lineTo(cx, cy - 12);
    ctx.lineTo(cx + 14, cy - 2);
    ctx.closePath();
    ctx.fill();
  },
  pond(ctx, x, y) {
    tileRenderers.sand_raked(ctx, x, y);
    const cx = x + TILE_W / 2;
    const cy = y + 2;
    ctx.fillStyle = '#2d88b8';
    ctx.beginPath();
    ctx.ellipse(cx, cy, 20, 10, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#c4ae82';
    ctx.lineWidth = 3;
    ctx.stroke();
  },
  cherry(ctx, x, y) {
    if (_plantsImg) { drawPlantSprite(ctx, 'cherry', x, y); return; }
    tileRenderers.grass(ctx, x, y);
    const cx = x + TILE_W / 2;
    const cy = y - 2;
    // Root tendrils
    ctx.save();
    ctx.strokeStyle = '#2e1506';
    ctx.lineCap = 'round';
    [[-18, 9], [-10, 11], [-2, 12], [8, 11], [16, 9]].forEach(([dx, dy], index) => {
      ctx.lineWidth = index === 2 ? 5 : 3.5;
      ctx.beginPath();
      ctx.moveTo(cx, cy + 5);
      ctx.quadraticCurveTo(cx + dx * 0.55, cy + 9, cx + dx, cy + dy);
      ctx.stroke();
    });
    ctx.strokeStyle = '#6a3c14';
    [[-18, 9], [-10, 11], [-2, 12], [8, 11], [16, 9]].forEach(([dx, dy], index) => {
      ctx.lineWidth = index === 2 ? 3 : 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy + 4);
      ctx.quadraticCurveTo(cx + dx * 0.55, cy + 8, cx + dx, cy + dy - 1);
      ctx.stroke();
    });
    ctx.restore();
    // Trunk
    drawDetailedTrunk(ctx, cx, cy, 10, '#7b4e2b', '#1e0c04',
      (canvasContext) => {
        canvasContext.beginPath();
        canvasContext.moveTo(cx, cy + 6);
        canvasContext.bezierCurveTo(cx - 4, cy - 8, cx + 6, cy - 22, cx + 2, cy - 42);
      });
    // Branches
    ctx.save();
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#180804';
    ctx.lineWidth = 7;
    ctx.beginPath();
    ctx.moveTo(cx - 1, cy - 24);
    ctx.bezierCurveTo(cx - 14, cy - 30, cx - 26, cy - 28, cx - 28, cy - 20);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 2, cy - 30);
    ctx.bezierCurveTo(cx + 14, cy - 36, cx + 26, cy - 34, cx + 28, cy - 26);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx, cy - 38);
    ctx.bezierCurveTo(cx - 6, cy - 46, cx - 4, cy - 54, cx - 2, cy - 58);
    ctx.stroke();
    ctx.strokeStyle = '#6a3c14';
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(cx - 1, cy - 24);
    ctx.bezierCurveTo(cx - 13, cy - 29, cx - 24, cy - 27, cx - 26, cy - 19);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 2, cy - 30);
    ctx.bezierCurveTo(cx + 13, cy - 35, cx + 25, cy - 33, cx + 27, cy - 25);
    ctx.stroke();
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(cx, cy - 38);
    ctx.bezierCurveTo(cx - 5, cy - 45, cx - 3, cy - 53, cx - 1, cy - 57);
    ctx.stroke();
    ctx.restore();
    // Foliage — cherry blossom pink
    [[cx - 28, cy - 26, 13, 8, 4, 0.0, '#d87098', '#f0a0c0', '#a04068'],
     [cx + 28, cy - 32, 12, 8, 3, 0.6, '#dc78a0', '#f2a8c4', '#a44870']].forEach(([fx, fy, radius, sides, jitter, angleOffset, mid, top, dark]) => {
      drawPolyFoliage(ctx, polyRing(fx, fy, radius, sides, jitter, angleOffset), mid, top, dark);
    });
    [[cx + 2, cy - 50, 18, 11, 6, 0.2, '#e890b0', '#fcc8e0', '#b05878'],
     [cx - 20, cy - 40, 15, 10, 5, 1.0, '#e488a8', '#f8c0d8', '#ac5474'],
     [cx + 22, cy - 40, 14, 10, 5, 0.7, '#e690b0', '#fac4dc', '#ae5676'],
     [cx - 6, cy - 64, 14, 10, 5, 1.8, '#ea98b8', '#fcd0e4', '#b45c7c'],
     [cx + 12, cy - 62, 13, 9, 5, 2.5, '#e890b0', '#fcc8e0', '#b05a78']].forEach(([fx, fy, radius, sides, jitter, angleOffset, mid, top, dark]) => {
      drawPolyFoliage(ctx, polyRing(fx, fy, radius, sides, jitter, angleOffset), mid, top, dark);
    });
    [[cx, cy - 72, 12, 8, 4, 0.3, '#f0a4c0', '#ffd8ec', '#bc6488'],
     [cx - 10, cy - 70, 10, 7, 3, 1.2, '#ee9cba', '#fed4e8', '#b86084'],
     [cx + 14, cy - 68, 10, 7, 3, 2.0, '#f0a0be', '#ffd6ea', '#ba6286']].forEach(([fx, fy, radius, sides, jitter, angleOffset, mid, top, dark]) => {
      drawPolyFoliage(ctx, polyRing(fx, fy, radius, sides, jitter, angleOffset), mid, top, dark);
    });
  },
  autumn_tree(ctx, x, y) {
    if (_plantsImg) { drawPlantSprite(ctx, 'autumn_tree', x, y); return; }
    tileRenderers.grass(ctx, x, y);
    const cx = x + TILE_W / 2;
    const cy = y - 2;
    // Root tendrils
    ctx.save();
    ctx.strokeStyle = '#2e1506';
    ctx.lineCap = 'round';
    [[-18, 9], [-10, 11], [-2, 12], [8, 11], [16, 9]].forEach(([dx, dy], index) => {
      ctx.lineWidth = index === 2 ? 5 : 3.5;
      ctx.beginPath();
      ctx.moveTo(cx, cy + 5);
      ctx.quadraticCurveTo(cx + dx * 0.55, cy + 9, cx + dx, cy + dy);
      ctx.stroke();
    });
    ctx.strokeStyle = '#6a3c14';
    [[-18, 9], [-10, 11], [-2, 12], [8, 11], [16, 9]].forEach(([dx, dy], index) => {
      ctx.lineWidth = index === 2 ? 3 : 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy + 4);
      ctx.quadraticCurveTo(cx + dx * 0.55, cy + 8, cx + dx, cy + dy - 1);
      ctx.stroke();
    });
    ctx.restore();
    // Trunk
    drawDetailedTrunk(ctx, cx, cy, 10, '#8c5a24', '#1e0c04',
      (canvasContext) => {
        canvasContext.beginPath();
        canvasContext.moveTo(cx, cy + 6);
        canvasContext.bezierCurveTo(cx - 4, cy - 8, cx + 6, cy - 22, cx + 2, cy - 42);
      });
    // Branches
    ctx.save();
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#180804';
    ctx.lineWidth = 7;
    ctx.beginPath();
    ctx.moveTo(cx - 1, cy - 24);
    ctx.bezierCurveTo(cx - 14, cy - 30, cx - 26, cy - 28, cx - 28, cy - 20);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 2, cy - 30);
    ctx.bezierCurveTo(cx + 14, cy - 36, cx + 26, cy - 34, cx + 28, cy - 26);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx, cy - 38);
    ctx.bezierCurveTo(cx - 6, cy - 46, cx - 4, cy - 54, cx - 2, cy - 58);
    ctx.stroke();
    ctx.strokeStyle = '#6a3c14';
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(cx - 1, cy - 24);
    ctx.bezierCurveTo(cx - 13, cy - 29, cx - 24, cy - 27, cx - 26, cy - 19);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 2, cy - 30);
    ctx.bezierCurveTo(cx + 13, cy - 35, cx + 25, cy - 33, cx + 27, cy - 25);
    ctx.stroke();
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(cx, cy - 38);
    ctx.bezierCurveTo(cx - 5, cy - 45, cx - 3, cy - 53, cx - 1, cy - 57);
    ctx.stroke();
    ctx.restore();
    // Foliage — autumn orange/gold
    [[cx - 28, cy - 26, 13, 8, 4, 0.0, '#c06010', '#e89028', '#6a2808'],
     [cx + 28, cy - 32, 12, 8, 3, 0.6, '#c86814', '#ec9830', '#702c0a']].forEach(([fx, fy, radius, sides, jitter, angleOffset, mid, top, dark]) => {
      drawPolyFoliage(ctx, polyRing(fx, fy, radius, sides, jitter, angleOffset), mid, top, dark);
    });
    [[cx + 2, cy - 50, 18, 11, 6, 0.2, '#d87818', '#f4b840', '#803408'],
     [cx - 20, cy - 40, 15, 10, 5, 1.0, '#d06c10', '#f0a830', '#783008'],
     [cx + 22, cy - 40, 14, 10, 5, 0.7, '#cc6810', '#ee9c28', '#742c08'],
     [cx - 6, cy - 64, 14, 10, 5, 1.8, '#dc7c1c', '#f8c048', '#843808'],
     [cx + 12, cy - 62, 13, 9, 5, 2.5, '#d4740c', '#f2b038', '#7e3208']].forEach(([fx, fy, radius, sides, jitter, angleOffset, mid, top, dark]) => {
      drawPolyFoliage(ctx, polyRing(fx, fy, radius, sides, jitter, angleOffset), mid, top, dark);
    });
    [[cx, cy - 72, 12, 8, 4, 0.3, '#e48820', '#fcd060', '#8c4010'],
     [cx - 10, cy - 70, 10, 7, 3, 1.2, '#e08018', '#f8c850', '#883c0c'],
     [cx + 14, cy - 68, 10, 7, 3, 2.0, '#e28420', '#facc58', '#8a3e0e']].forEach(([fx, fy, radius, sides, jitter, angleOffset, mid, top, dark]) => {
      drawPolyFoliage(ctx, polyRing(fx, fy, radius, sides, jitter, angleOffset), mid, top, dark);
    });
  },
  oak(ctx, x, y) {
    if (_plantsImg) { drawPlantSprite(ctx, 'oak', x, y); return; }
    tileRenderers.stone_path(ctx, x, y);
    const cx = x + TILE_W / 2;
    const cy = y - 2;
    ctx.save();
    ctx.strokeStyle = '#2e1506';
    ctx.lineCap = 'round';
    [[-18, 9], [-10, 11], [-2, 12], [8, 11], [16, 9]].forEach(([dx, dy], index) => {
      ctx.lineWidth = index === 2 ? 5 : 3.5;
      ctx.beginPath();
      ctx.moveTo(cx, cy + 5);
      ctx.quadraticCurveTo(cx + dx * 0.55, cy + 9, cx + dx, cy + dy);
      ctx.stroke();
    });
    ctx.strokeStyle = '#6a3c14';
    [[-18, 9], [-10, 11], [-2, 12], [8, 11], [16, 9]].forEach(([dx, dy], index) => {
      ctx.lineWidth = index === 2 ? 3 : 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy + 4);
      ctx.quadraticCurveTo(cx + dx * 0.55, cy + 8, cx + dx, cy + dy - 1);
      ctx.stroke();
    });
    ctx.restore();
    drawDetailedTrunk(ctx, cx, cy, 10, '#8c5a24', '#1e0c04',
      (canvasContext) => {
        canvasContext.beginPath();
        canvasContext.moveTo(cx, cy + 6);
        canvasContext.bezierCurveTo(cx - 4, cy - 8, cx + 6, cy - 22, cx + 2, cy - 42);
      });
    ctx.save();
    ctx.strokeStyle = 'rgba(15,5,2,0.4)';
    ctx.lineWidth = 1.2;
    ctx.lineCap = 'round';
    [[cx - 2, cy + 2, cx - 5, cy - 14], [cx + 3, cy - 1, cx + 5, cy - 18], [cx - 1, cy - 16, cx - 3, cy - 30], [cx + 2, cy - 20, cx + 5, cy - 36]].forEach(([x1, y1, x2, y2]) => {
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    });
    ctx.restore();
    ctx.save();
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#180804';
    ctx.lineWidth = 7;
    ctx.beginPath();
    ctx.moveTo(cx - 1, cy - 24);
    ctx.bezierCurveTo(cx - 14, cy - 30, cx - 26, cy - 28, cx - 28, cy - 20);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 2, cy - 30);
    ctx.bezierCurveTo(cx + 14, cy - 36, cx + 26, cy - 34, cx + 28, cy - 26);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx, cy - 38);
    ctx.bezierCurveTo(cx - 6, cy - 46, cx - 4, cy - 54, cx - 2, cy - 58);
    ctx.stroke();
    ctx.strokeStyle = '#6a3c14';
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(cx - 1, cy - 24);
    ctx.bezierCurveTo(cx - 13, cy - 29, cx - 24, cy - 27, cx - 26, cy - 19);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 2, cy - 30);
    ctx.bezierCurveTo(cx + 13, cy - 35, cx + 25, cy - 33, cx + 27, cy - 25);
    ctx.stroke();
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(cx, cy - 38);
    ctx.bezierCurveTo(cx - 5, cy - 45, cx - 3, cy - 53, cx - 1, cy - 57);
    ctx.stroke();
    ctx.strokeStyle = '#5a3010';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(cx - 24, cy - 22);
    ctx.bezierCurveTo(cx - 30, cy - 30, cx - 28, cy - 38, cx - 25, cy - 42);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 25, cy - 28);
    ctx.bezierCurveTo(cx + 31, cy - 36, cx + 29, cy - 44, cx + 26, cy - 48);
    ctx.stroke();
    ctx.restore();
    drawBaseRocks(ctx, cx, cy + 6);
    [[cx - 28, cy - 26, 13, 8, 4, 0.0, '#348020', '#5a9e38', '#124010'],
     [cx + 28, cy - 32, 12, 8, 3, 0.6, '#368022', '#5c9e3a', '#124010'],
     [cx - 16, cy - 48, 11, 7, 3, 1.1, '#3c8828', '#64a840', '#165214']].forEach(([fx, fy, radius, sides, jitter, angleOffset, mid, top, dark]) => {
      drawPolyFoliage(ctx, polyRing(fx, fy, radius, sides, jitter, angleOffset), mid, top, dark);
    });
    [[cx + 2, cy - 50, 18, 11, 6, 0.2, '#52a838', '#8ed060', '#1c5818'],
     [cx - 20, cy - 40, 15, 10, 5, 1.0, '#4ea034', '#88ca58', '#1a5416'],
     [cx + 22, cy - 40, 14, 10, 5, 0.7, '#52a436', '#8ccc5c', '#1c5616'],
     [cx - 6, cy - 64, 14, 10, 5, 1.8, '#56ac3c', '#90d464', '#1e5c1a'],
     [cx + 12, cy - 62, 13, 9, 5, 2.5, '#52a838', '#8ed060', '#1c5a18']].forEach(([fx, fy, radius, sides, jitter, angleOffset, mid, top, dark]) => {
      drawPolyFoliage(ctx, polyRing(fx, fy, radius, sides, jitter, angleOffset), mid, top, dark);
    });
    [[cx, cy - 72, 12, 8, 4, 0.3, '#64b844', '#a4dc6c', '#225e1c'],
     [cx - 10, cy - 70, 10, 7, 3, 1.2, '#5eb440', '#9cd866', '#205a1a'],
     [cx + 14, cy - 68, 10, 7, 3, 2.0, '#60b640', '#a0da68', '#225c1c']].forEach(([fx, fy, radius, sides, jitter, angleOffset, mid, top, dark]) => {
      drawPolyFoliage(ctx, polyRing(fx, fy, radius, sides, jitter, angleOffset), mid, top, dark);
    });
  },
  fruit_tree(ctx, x, y) {
    if (_plantsImg) { drawPlantSprite(ctx, 'fruit_tree', x, y); return; }
    tileRenderers.oak(ctx, x, y);
  },
  palm(ctx, x, y) {
    if (_plantsImg) { drawPlantSprite(ctx, 'palm', x, y); return; }
    tileRenderers.oak(ctx, x, y);
  },
  // Species families (treeA through treeD, 5 growth stages each)
  treeA_1(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeA_1', x, y); return; } },
  treeA_2(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeA_2', x, y); return; } },
  treeA_3(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeA_3', x, y); return; } },
  treeA_4(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeA_4', x, y); return; } },
  treeA_5(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeA_5', x, y); return; } },
  treeB_1(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeB_1', x, y); return; } },
  treeB_2(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeB_2', x, y); return; } },
  treeB_3(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeB_3', x, y); return; } },
  treeB_4(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeB_4', x, y); return; } },
  treeB_5(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeB_5', x, y); return; } },
  treeC_1(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeC_1', x, y); return; } },
  treeC_2(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeC_2', x, y); return; } },
  treeC_3(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeC_3', x, y); return; } },
  treeC_4(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeC_4', x, y); return; } },
  treeC_5(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeC_5', x, y); return; } },
  treeD_1(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeD_1', x, y); return; } },
  treeD_2(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeD_2', x, y); return; } },
  treeD_3(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeD_3', x, y); return; } },
  treeD_4(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeD_4', x, y); return; } },
  treeD_5(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'treeD_5', x, y); return; } },
  // Legacy family renderers (backward compat for old saved grids)
  vine_1(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'vine_1', x, y); return; } },
  vine_2(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'vine_2', x, y); return; } },
  vine_3(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'vine_3', x, y); return; } },
  // Legacy pepper family
  pepper_1(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'pepper_1', x, y); return; } },
  pepper_2(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'pepper_2', x, y); return; } },
  pepper_3(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'pepper_3', x, y); return; } },
  // Legacy herb family
  herb_1(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'herb_1', x, y); return; } },
  herb_2(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'herb_2', x, y); return; } },
  herb_3(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'herb_3', x, y); return; } },
  // Legacy flower family
  flower_1(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'flower_1', x, y); return; } },
  flower_2(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'flower_2', x, y); return; } },
  flower_3(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'flower_3', x, y); return; } },
  flower_4(ctx, x, y) { if (_plantsImg) { drawPlantSprite(ctx, 'flower_4', x, y); return; } },
  oak_autumn(ctx, x, y) {
    tileRenderers.grass(ctx, x, y);
    const cx = x + TILE_W / 2;
    const cy = y - 2;
    ctx.save();
    ctx.strokeStyle = '#2e1506';
    ctx.lineCap = 'round';
    [[-18, 9], [-10, 11], [-2, 12], [8, 11], [16, 9]].forEach(([dx, dy], index) => {
      ctx.lineWidth = index === 2 ? 5 : 3.5;
      ctx.beginPath();
      ctx.moveTo(cx, cy + 5);
      ctx.quadraticCurveTo(cx + dx * 0.55, cy + 9, cx + dx, cy + dy);
      ctx.stroke();
    });
    ctx.strokeStyle = '#6a3c14';
    [[-18, 9], [-10, 11], [-2, 12], [8, 11], [16, 9]].forEach(([dx, dy], index) => {
      ctx.lineWidth = index === 2 ? 3 : 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy + 4);
      ctx.quadraticCurveTo(cx + dx * 0.55, cy + 8, cx + dx, cy + dy - 1);
      ctx.stroke();
    });
    ctx.restore();
    drawDetailedTrunk(ctx, cx, cy, 10, '#8c5a24', '#1e0c04',
      (canvasContext) => {
        canvasContext.beginPath();
        canvasContext.moveTo(cx, cy + 6);
        canvasContext.bezierCurveTo(cx - 4, cy - 8, cx + 6, cy - 22, cx + 2, cy - 42);
      });
    ctx.save();
    ctx.strokeStyle = 'rgba(15,5,2,0.4)';
    ctx.lineWidth = 1.2;
    ctx.lineCap = 'round';
    [[cx - 2, cy + 2, cx - 5, cy - 14], [cx + 3, cy - 1, cx + 5, cy - 18], [cx - 1, cy - 16, cx - 3, cy - 30], [cx + 2, cy - 20, cx + 5, cy - 36]].forEach(([x1, y1, x2, y2]) => {
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    });
    ctx.restore();
    ctx.save();
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#180804';
    ctx.lineWidth = 7;
    ctx.beginPath();
    ctx.moveTo(cx - 1, cy - 24);
    ctx.bezierCurveTo(cx - 14, cy - 30, cx - 26, cy - 28, cx - 28, cy - 20);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 2, cy - 30);
    ctx.bezierCurveTo(cx + 14, cy - 36, cx + 26, cy - 34, cx + 28, cy - 26);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx, cy - 38);
    ctx.bezierCurveTo(cx - 6, cy - 46, cx - 4, cy - 54, cx - 2, cy - 58);
    ctx.stroke();
    ctx.strokeStyle = '#6a3c14';
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(cx - 1, cy - 24);
    ctx.bezierCurveTo(cx - 13, cy - 29, cx - 24, cy - 27, cx - 26, cy - 19);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 2, cy - 30);
    ctx.bezierCurveTo(cx + 13, cy - 35, cx + 25, cy - 33, cx + 27, cy - 25);
    ctx.stroke();
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(cx, cy - 38);
    ctx.bezierCurveTo(cx - 5, cy - 45, cx - 3, cy - 53, cx - 1, cy - 57);
    ctx.stroke();
    ctx.strokeStyle = '#5a3010';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(cx - 24, cy - 22);
    ctx.bezierCurveTo(cx - 30, cy - 30, cx - 28, cy - 38, cx - 25, cy - 42);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + 25, cy - 28);
    ctx.bezierCurveTo(cx + 31, cy - 36, cx + 29, cy - 44, cx + 26, cy - 48);
    ctx.stroke();
    ctx.restore();
    drawBaseRocks(ctx, cx, cy + 6);
    [[cx - 28, cy - 26, 13, 8, 4, 0.0, '#882008', '#c85010', '#4a0e04'],
     [cx + 28, cy - 32, 12, 8, 3, 0.6, '#942a0a', '#d85c14', '#501204']].forEach(([fx, fy, radius, sides, jitter, angleOffset, mid, top, dark]) => {
      drawPolyFoliage(ctx, polyRing(fx, fy, radius, sides, jitter, angleOffset), mid, top, dark);
    });
    [[cx + 2, cy - 50, 18, 11, 6, 0.2, '#d46010', '#f8c040', '#801c04'],
     [cx - 20, cy - 40, 15, 10, 5, 1.0, '#c44c08', '#f09028', '#781604'],
     [cx + 22, cy - 40, 14, 10, 5, 0.7, '#b84008', '#ec8020', '#701204'],
     [cx - 6, cy - 64, 14, 10, 5, 1.8, '#d85c10', '#fcc848', '#861c04'],
     [cx + 12, cy - 62, 13, 9, 5, 2.5, '#cc5010', '#f4a030', '#7c1804']].forEach(([fx, fy, radius, sides, jitter, angleOffset, mid, top, dark]) => {
      drawPolyFoliage(ctx, polyRing(fx, fy, radius, sides, jitter, angleOffset), mid, top, dark);
    });
    [[cx, cy - 72, 12, 8, 4, 0.3, '#e07018', '#fce060', '#902808'],
     [cx - 10, cy - 70, 10, 7, 3, 1.2, '#d46412', '#f8c840', '#841e04'],
     [cx + 14, cy - 68, 10, 7, 3, 2.0, '#dc6c14', '#fcd050', '#8a2204']].forEach(([fx, fy, radius, sides, jitter, angleOffset, mid, top, dark]) => {
      drawPolyFoliage(ctx, polyRing(fx, fy, radius, sides, jitter, angleOffset), mid, top, dark);
    });
    ctx.save();
    [[cx + 28, cy - 20, 0.4, '#e06010'], [cx - 26, cy - 12, -0.7, '#d04808'], [cx + 22, cy + 2, 1.2, '#f0a020'],
     [cx - 18, cy + 4, -0.3, '#e87018'], [cx + 34, cy - 34, 0.6, '#f8b828'], [cx - 30, cy - 26, -0.5, '#cc4010'],
     [cx + 18, cy - 8, 0.9, '#f09020'], [cx - 22, cy - 38, -0.8, '#dc6018']].forEach(([leafX, leafY, rotation, color]) => {
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.88;
      ctx.save();
      ctx.translate(leafX, leafY);
      ctx.rotate(rotation);
      ctx.beginPath();
      ctx.moveTo(0, -6);
      ctx.bezierCurveTo(3, -4, 6, -5, 5, -2);
      ctx.bezierCurveTo(7, -1, 7, 2, 5, 2);
      ctx.bezierCurveTo(5, 5, 2, 6, 0, 6);
      ctx.bezierCurveTo(-2, 6, -5, 5, -5, 2);
      ctx.bezierCurveTo(-7, 2, -7, -1, -5, -2);
      ctx.bezierCurveTo(-6, -5, -3, -4, 0, -6);
      ctx.fill();
      ctx.strokeStyle = 'rgba(80,20,0,0.35)';
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(0, -5);
      ctx.lineTo(0, 5);
      ctx.stroke();
      ctx.restore();
    });
    ctx.restore();
  },
  pine(ctx, x, y) {
    tileRenderers.grass(ctx, x, y);
    const cx = x + TILE_W / 2;
    const cy = y - 8;
    ctx.strokeStyle = '#704929';
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(cx, cy + 18);
    ctx.lineTo(cx, cy - 34);
    ctx.stroke();
    ['#3f7e34', '#467f38', '#4e8c40'].forEach((color, index) => {
      const topY = cy - 26 + index * 12;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(cx, topY - 16);
      ctx.lineTo(cx + 18 - index * 2, topY + 8);
      ctx.lineTo(cx - 18 + index * 2, topY + 8);
      ctx.closePath();
      ctx.fill();
    });
  },
};

function safeGetContext(canvas) {
  try {
    return canvas.getContext('2d');
  } catch (error) {
    return null;
  }
}

const TILE_IMG_W = TILE_W;
const TILE_IMG_H = TILE_H + TILE_DEPTH;

function IsometricGardenPrototype() {
  const [grid, setGrid] = useState(() => createInitialGarden());
  const [selectedTile, setSelectedTile] = useState('grass');
  const [transitions, setTransitions] = useState(() => Array.from({ length: ROWS }, () => Array(COLS).fill(null)));
  const [version, setVersion] = useState(0);
  const [isPainting, setIsPainting] = useState(false);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const animationStartRef = useRef(null);
  const grassImgRef = useRef(null);

  useEffect(() => {
    const img = new Image();
    img.src = grassTileImg;
    img.onload = () => {
      grassImgRef.current = img;
      setVersion((v) => v + 1);
    };
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = plantsImgUrl;
    img.onload = () => {
      _plantsImg = img;
      setVersion((v) => v + 1);
    };
  }, []);

  const groupedTiles = useMemo(() => groupTiles(), []);
  const tileCounts = useMemo(() => countTiles(grid), [grid]);
  const growthSummary = useMemo(() => getGrowthSummary(grid), [grid]);

  const drawGrid = useCallback((ctx) => {
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    for (let row = 0; row < ROWS; row += 1) {
      for (let col = 0; col < COLS; col += 1) {
        const point = isoPoint(col, row, OFF_X, OFF_Y);
        const x = point.x - TILE_W / 2;
        const y = point.y;
        const transition = transitions[row][col];

        if (transition && transition.t < 1) {
          const ease = transition.t < 0.5
            ? 4 * transition.t * transition.t * transition.t
            : 1 - Math.pow(-2 * transition.t + 2, 3) / 2;
          const spring = ease < 0.85 ? ease : ease + Math.sin(((ease - 0.85) / 0.15) * Math.PI) * 0.06;
          const tileCenterX = x + TILE_W / 2;
          const tileCenterY = y + TILE_H / 4;

          if (transition.from && tileRenderers[transition.from]) {
            const shrink = Math.max(0, 1 - ease * 1.4);
            ctx.save();
            ctx.globalAlpha = Math.max(0, 1 - ease * 2);
            ctx.translate(tileCenterX, tileCenterY);
            ctx.scale(shrink, shrink);
            ctx.translate(-tileCenterX, -tileCenterY);
            if (transition.from === 'grass' && grassImgRef.current) {
              ctx.drawImage(grassImgRef.current, x, y - TILE_H / 2, TILE_IMG_W, TILE_IMG_H);
            } else {
              tileRenderers[transition.from](ctx, x, y);
            }
            ctx.restore();
          }

          if (transition.to && tileRenderers[transition.to]) {
            ctx.save();
            ctx.globalAlpha = Math.min(1, ease * 2);
            ctx.translate(tileCenterX, tileCenterY);
            ctx.scale(Math.min(1.12, spring), Math.min(1.12, spring));
            ctx.translate(-tileCenterX, -tileCenterY);
            if (transition.to === 'grass' && grassImgRef.current) {
              ctx.drawImage(grassImgRef.current, x, y - TILE_H / 2, TILE_IMG_W, TILE_IMG_H);
            } else {
              tileRenderers[transition.to](ctx, x, y);
            }
            ctx.restore();
          }
        } else {
          const tileType = grid[row][col];
          if (tileType === 'grass' && grassImgRef.current) {
            ctx.drawImage(grassImgRef.current, x, y - TILE_H / 2, TILE_IMG_W, TILE_IMG_H);
          } else {
            tileRenderers[tileType]?.(ctx, x, y);
          }
        }
      }
    }

    ctx.save();
    ctx.globalAlpha = 0.07;
    ctx.strokeStyle = '#4a7040';
    ctx.lineWidth = 0.5;
    for (let row = 0; row < ROWS; row += 1) {
      for (let col = 0; col < COLS; col += 1) {
        const point = isoPoint(col, row, OFF_X, OFF_Y);
        const x = point.x - TILE_W / 2;
        const y = point.y;
        ctx.beginPath();
        ctx.moveTo(x + TILE_W / 2, y - TILE_H / 2);
        ctx.lineTo(x + TILE_W, y);
        ctx.lineTo(x + TILE_W / 2, y + TILE_H / 2);
        ctx.lineTo(x, y);
        ctx.closePath();
        ctx.stroke();
      }
    }
    ctx.restore();
  }, [grid, transitions]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const ctx = safeGetContext(canvas);
    if (!ctx) {
      return undefined;
    }

    drawGrid(ctx);
    return undefined;
  }, [drawGrid, version]);

  useEffect(() => {
    const hasActiveTransitions = transitions.some((row) => row.some((transition) => transition));
    if (!hasActiveTransitions) {
      animationStartRef.current = null;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return undefined;
    }

    const tick = (timestamp) => {
      if (!animationStartRef.current) {
        animationStartRef.current = timestamp;
      }
      const elapsed = timestamp - animationStartRef.current;
      let anyActive = false;

      setTransitions((currentTransitions) => currentTransitions.map((rowTransitions, rowIndex) => (
        rowTransitions.map((transition, colIndex) => {
          if (!transition) {
            return null;
          }
          const delay = (rowIndex + colIndex) * STAGGER_MS;
          const localElapsed = Math.max(0, elapsed - delay);
          const t = Math.min(1, localElapsed / ANIMATION_DURATION);
          if (t < 1) {
            anyActive = true;
            return { ...transition, t };
          }
          return null;
        })
      )));
      setVersion((currentVersion) => currentVersion + 1);

      if (anyActive) {
        animationFrameRef.current = requestAnimationFrame(tick);
      } else {
        animationFrameRef.current = null;
        animationStartRef.current = null;
      }
    };

    animationFrameRef.current = requestAnimationFrame(tick);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [transitions]);

  const startTransition = useCallback((nextGrid) => {
    const nextTransitions = Array.from({ length: ROWS }, (_, row) => Array.from({ length: COLS }, (_, col) => {
      const from = grid[row][col];
      const to = nextGrid[row][col];
      if (from === to) {
        return null;
      }
      return { from, to, t: 0 };
    }));

    setGrid(nextGrid);
    setTransitions(nextTransitions);
    setVersion((currentVersion) => currentVersion + 1);
  }, [grid]);

  const handleGrowAll = useCallback(() => {
    setGrid((currentGrid) => {
      const result = stepGrowth(currentGrid);
      if (result.changed) {
        setVersion((v) => v + 1);
        return result.grid;
      }
      return currentGrid;
    });
  }, []);

  const handleClear = useCallback(() => {
    startTransition(clearGarden(grid));
  }, [grid, startTransition]);

  const handleReset = useCallback(() => {
    startTransition(createInitialGarden());
  }, [startTransition]);

  const handleRandomize = useCallback(() => {
    startTransition(randomizeGarden());
  }, [startTransition]);

  const paintAtPointer = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const mouseX = (clientX - rect.left) * scaleX;
    const mouseY = (clientY - rect.top) * scaleY;
    const { row, col } = getGridCell(mouseX, mouseY, OFF_X, OFF_Y);

    if (row < 0 || row >= ROWS || col < 0 || col >= COLS) {
      return;
    }

    if (grid[row][col] === selectedTile) {
      return;
    }

    const nextGrid = grid.map((currentRow, currentRowIndex) => currentRow.map((tileId, currentColIndex) => (
      currentRowIndex === row && currentColIndex === col ? selectedTile : tileId
    )));
    startTransition(nextGrid);
  }, [grid, selectedTile, startTransition]);

  const handleMouseDown = useCallback((event) => {
    setIsPainting(true);
    paintAtPointer(event.clientX, event.clientY);
  }, [paintAtPointer]);

  const handleMouseMove = useCallback((event) => {
    if (!isPainting) {
      return;
    }
    paintAtPointer(event.clientX, event.clientY);
  }, [isPainting, paintAtPointer]);

  const handleTouchStart = useCallback((event) => {
    setIsPainting(true);
    const touch = event.touches[0];
    if (touch) {
      paintAtPointer(touch.clientX, touch.clientY);
    }
  }, [paintAtPointer]);

  const handleTouchMove = useCallback((event) => {
    if (!isPainting) {
      return;
    }
    const touch = event.touches[0];
    if (touch) {
      event.preventDefault();
      paintAtPointer(touch.clientX, touch.clientY);
    }
  }, [isPainting, paintAtPointer]);

  useEffect(() => {
    const stopPainting = () => setIsPainting(false);
    window.addEventListener('mouseup', stopPainting);
    window.addEventListener('touchend', stopPainting);
    return () => {
      window.removeEventListener('mouseup', stopPainting);
      window.removeEventListener('touchend', stopPainting);
    };
  }, []);

  const totalGrowthTiles = growthSummary.reduce((sum, entry) => sum + entry.count, 0);
  const matureTrees = (tileCounts.ancient_bonsai || 0) + (tileCounts.oak || 0) + (tileCounts.oak_autumn || 0) + (tileCounts.pine || 0) + (tileCounts.cherry || 0) + (tileCounts.autumn_tree || 0);

  return (
    <div style={{ width: '100%', maxWidth: 1180, display: 'grid', gap: 24 }}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <ControlButton label="Clear Grid" onClick={handleClear} testId="garden-clear" />
          <ControlButton label="Grow" onClick={handleGrowAll} accent testId="garden-grow" />
          <ControlButton label="Randomize" onClick={handleRandomize} testId="garden-randomize" />
          <ControlButton label="Reset Scene" onClick={handleReset} dark testId="garden-reset" />
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', color: '#6b7280', fontSize: 13 }}>
          <span data-testid="selected-tile">Selected tile: {selectedTile}</span>
          <span data-testid="growth-count">Growth tiles: {totalGrowthTiles}</span>
          <span data-testid="mature-count">Mature trees: {matureTrees}</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(270px, 320px)', gap: 24, alignItems: 'start' }}>
        <div style={{ background: '#faf7f1', border: '1px solid #e6dfd1', borderRadius: 24, padding: 16, overflow: 'auto' }}>
          <canvas
            ref={canvasRef}
            width={CANVAS_W}
            height={CANVAS_H}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            data-testid="garden-canvas"
            style={{ width: '100%', maxWidth: 860, display: 'block', margin: '0 auto', borderRadius: 18, cursor: 'crosshair', touchAction: 'none', background: 'linear-gradient(180deg, #f5efe2 0%, #efe7da 100%)' }}
          />
        </div>

        <div style={{ display: 'grid', gap: 18 }}>
          {Object.entries(groupedTiles).map(([groupName, tiles]) => (
            <section key={groupName} style={{ background: '#ffffff', border: '1px solid #ebe4d8', borderRadius: 18, padding: 14 }}>
              <h3 style={{ fontSize: 12, color: '#665c4d', letterSpacing: '0.18em', textTransform: 'uppercase', margin: 0 }}>
                {groupName === 'Growth' ? 'Growth Chain' : groupName}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginTop: 12 }}>
                {tiles.map((tile) => (
                  <button
                    key={tile.id}
                    type="button"
                    onClick={() => setSelectedTile(tile.id)}
                    data-testid={`tile-${tile.id}`}
                    style={{
                      borderRadius: 12,
                      border: selectedTile === tile.id ? '1px solid #264d2c' : '1px solid #ddd4c3',
                      background: selectedTile === tile.id ? '#f0f8ee' : '#fffdf8',
                      color: '#352f27',
                      padding: '10px 12px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'grid',
                      gap: 4,
                    }}
                  >
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{tile.label}</span>
                    <span style={{ fontSize: 11, color: '#7d7467' }}>{tile.id}</span>
                  </button>
                ))}
              </div>
            </section>
          ))}

          <section style={{ background: '#ffffff', border: '1px solid #ebe4d8', borderRadius: 18, padding: 14 }}>
            <h3 style={{ fontSize: 12, color: '#665c4d', letterSpacing: '0.18em', textTransform: 'uppercase', margin: 0 }}>
              Growth Summary
            </h3>
            <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
              {growthSummary.map((entry) => (
                <div key={entry.tileId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#41392f' }}>
                  <span>{entry.tileId}</span>
                  <strong data-testid={`summary-${entry.tileId}`}>{entry.count}</strong>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function ControlButton({ label, onClick, accent = false, dark = false, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{
        padding: '10px 18px',
        borderRadius: 12,
        border: accent || dark ? 'none' : '1px solid #d6cfbf',
        background: dark ? '#203328' : accent ? '#2d6a4f' : '#fff',
        color: accent || dark ? '#fff' : '#2c2a28',
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: 13,
      }}
    >
      {label}
    </button>
  );
}

export default IsometricGardenPrototype;
export { tileRenderers };